import type { StandingEntry } from "./standings.js";

/**
 * Don mode — the app's one easter egg, in the part of it the Worker and the browser
 * both need.
 *
 * Don asked to never lose. This is the closest thing to that a scoring app can
 * honestly offer: hold down a player called Don on the standings and he robs the
 * field — every other player's points fly across the board into his total, and he sits
 * on top of them for a minute, on every phone in the group at once. Then it expires,
 * everyone gets their points back, and that's the tournament's one go.
 *
 * Two rules keep the joke from becoming a bug report:
 *
 *  - **Nothing is scored.** These are transforms on a copy of the standings, applied
 *    for one list on one screen. No score, round or point table is touched, so the
 *    round cards, the breakdown screens and both exports carry on showing the real
 *    thing throughout — which is what makes it read as a gag rather than as a fault.
 *  - **Once per tournament.** The activation lives on the tournament record in the
 *    Durable Object, which is what makes it both shared and single-use: a device with
 *    a cleared browser can't have another go, and a reload mid-reign rejoins the same
 *    minute rather than restarting it.
 *
 * The frontend half — the gesture, the flight animation and the countdown — is in
 * `frontend/src/donMode.ts`.
 */

/** How long the reign lasts. Long enough to show the group, short enough that it's
 * plainly a bit rather than a broken leaderboard. */
export const DON_MODE_MS = 60_000;

/**
 * Don, in every form a scorecard is likely to spell him.
 *
 * "Don" plus the diminutives that get typed into a player list — Donny, Donnie, Donn,
 * Donno, Donald, Donaldo, and the ones his mates invent (Donster, Donmeister, Donzo).
 * Deliberately a suffix list rather than "any word starting with Don": that would
 * swallow Donna and Donatello, who are not Don and did not ask for this.
 */
const DON_NAMES = /^don(ald|aldo|n|nie|ny|no|o|z|zo|ster|meister)?$/i;

/**
 * Whether a player counts as a Don.
 *
 * Any whole word of the name qualifies the player, so "Don", "don", "DONNY",
 * "Don Fletcher", "Big Don", "Don-Fletcher" and "Don's" all match — splitting on
 * non-letters rather than on spaces is what makes the hyphenated and possessive
 * spellings work, since a roster is typed by whoever set the game up.
 *
 * Checked in the Durable Object as well as in the browser: now that this moves every
 * phone in the group, "only a Don" has to be a rule rather than a UI state a modified
 * client could ignore.
 */
export function isDon(name: string): boolean {
  return name.split(/[^\p{L}]+/u).some((word) => DON_NAMES.test(word));
}

/**
 * The loot. Every point on the board is now Don's, and everyone else is on nothing.
 *
 * Summing rather than assigning a made-up number keeps the gag legible: the total he
 * ends up holding is visibly the sum of the column it came from, so the board reads as
 * a robbery rather than as a scoring bug. Placements are left alone here — the theft
 * and the climb to the top are two separate beats, and this is the first.
 */
export function stealPoints(standings: StandingEntry[], thiefId: string): StandingEntry[] {
  if (!standings.some((s) => s.playerId === thiefId)) return standings;

  const pot = standings.reduce((sum, s) => sum + s.totalPoints, 0);
  return standings.map((s) => ({
    ...s,
    totalPoints: s.playerId === thiefId ? pot : 0,
  }));
}

/**
 * The standings as Don wishes they were: him first, everyone else shuffled down a
 * place and otherwise in the order they earned.
 *
 * The rest are renumbered by the same competition ranking the real board uses —
 * players level with the one above them share that place, and the next player takes
 * the place their position gives them. Adding one to everybody would have been
 * shorter, but it leaves a hole where Don was standing: a board that really reads
 * 1, 1, 3, 4 would come back 2, 2, 5 under him, with 4th belonging to nobody.
 */
export function crownStandings(standings: StandingEntry[], playerId: string): StandingEntry[] {
  const crowned = standings.find((s) => s.playerId === playerId);
  if (!crowned) return standings;

  const rest = standings.filter((s) => s.playerId !== playerId);
  let placement = 1;

  return [
    { ...crowned, placement: 1 },
    ...rest.map((s, i) => {
      const tiedWithAbove = i > 0 && s.placement === rest[i - 1]!.placement;
      if (!tiedWithAbove) placement = i + 2;
      return { ...s, placement };
    }),
  ];
}
